import React, { useState, useEffect } from 'react';
import api from '../api/api';
import RecipeTree from '../components/utils/RecipeTree';
import { Loader2, Play, Edit3, CheckCircle } from 'lucide-react';
import Seo from '../components/Seo';

const UserRecipesPage = () => {
  const [recipeTrees, setRecipeTrees] = useState([]);
  const [readyToCookList, setReadyToCookList] = useState([]);
  const [finishedList, setFinishedList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const seo = (
    <Seo
      title="Mes recettes"
      description="Retrouvez vos recettes générées par YAQAM, vos variantes et vos préparations en cours."
      breadcrumb
    />
  );

  useEffect(() => {
    const fetchRecipeTrees = async () => {
      try {
        const { data } = await api.get('/api/meals/user-trees');
        setRecipeTrees(data.recipeTrees);
        setReadyToCookList(data.readyToCookList);
        setFinishedList(data.finishedList);
      } catch (error) {
        console.error('Error fetching recipe trees:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecipeTrees();
  }, []);

  if (isLoading) {
    return (
      <>
        {seo}
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#87A96B] mx-auto mb-4"></div>
            <p className="text-[#2C3E50]">Chargement de vos recettes...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {seo}
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 flex flex-col items-center justify-center">
          <img
            src="/img/my-recipes.png"
            alt="Chef"
            className="w-40 h-40 rounded-full shadow-xl mb-6 object-cover mx-auto"
          />
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 text-center leading-tight text-[#2C3E50]">
            Toutes {" "}
            <span className="text-[#87A96B]">mes recettes</span>{" "}
            en un seul endroit
          </h1>
          <p className="text-lg text-center text-gray-600">
            Explorer l'évolution de vos créations culinaires.
          </p>
        </div>

        {/* Ready to Cook */}
        <section className="mb-12 bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold flex items-center text-[#87A96B]">
              <Play size={24} className="mr-2" /> Recettes à faire
            </h2>
            <a
              href="/cooking-assistant"
              className="flex items-center px-6 py-3 bg-[#87A96B] text-white rounded-full font-medium hover:bg-[#87A96B]/90 transition-colors duration-200 shadow-md"
            >
              <Play size={18} className="mr-2" /> Cuisiner
            </a>
          </div>
          <div className="space-y-6">
            {readyToCookList.length ? (
              readyToCookList.reduce((acc, val) => [val, ...acc], []).map((tree) => (
                <RecipeTree
                  key={tree.id}
                  familyTree={tree}
                  currentRecipeId={tree.id}
                  currentPage={{ type: 'recipesList' }}
                  className="rounded-lg p-4 hover:shadow-md transition-shadow bg-gray-50 border border-gray-100"
                />
              ))
            ) : (
              <p className="text-center italic text-gray-600">Aucune recette à faire.</p>
            )}
          </div>
        </section>

        {/* In Progress */}
        <section className="mb-12 bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
          <h2 className="text-2xl font-semibold flex items-center mb-6 text-[#E67E22]">
            <Edit3 size={24} className="mr-2" /> Recettes en cours
          </h2>
          <div className="space-y-6">
            {recipeTrees
              .map(({ tree }) => tree).reduce((acc, val) => [val, ...acc], [])
              .filter((tree) => !readyToCookList.some((r) => r.id === tree.id))
              .map((tree) => (
                <RecipeTree
                  key={tree.id}
                  familyTree={tree}
                  currentRecipeId={tree.id}
                  currentPage={{ type: 'recipesList' }}
                  className="rounded-lg p-4 hover:shadow-md transition-shadow bg-gray-50 border border-gray-100"
                />
              ))}
            {recipeTrees.filter(({ tree }) => !tree.status).length === 0 && (
              <p className="text-center italic text-gray-600">
                Aucune recette en cours de création.
              </p>
            )}
          </div>
        </section>

        {/* Finished */}
        <section className="mb-12 bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
          <h2 className="text-2xl font-semibold flex items-center mb-6 text-[#2C3E50]">
            <CheckCircle size={24} className="mr-2" /> Recettes terminées
          </h2>
          <div className="space-y-6">
            {finishedList.length ? (
              finishedList.reduce((acc, val) => [val, ...acc], []).map((tree) => (
                <RecipeTree
                  key={tree.id}
                  familyTree={tree}
                  currentRecipeId={tree.id}
                  currentPage={{ type: 'recipesList' }}
                  className="rounded-lg p-4 hover:shadow-md transition-shadow bg-gray-50 border border-gray-100"
                />
              ))
            ) : (
              <p className="text-center italic text-gray-600">Aucune recette terminée.</p>
            )}
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default UserRecipesPage;
